import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { ArrowLeft, RefreshCw, ExternalLink, Mail } from "lucide-react";
import { toast } from "sonner";

const formatCurrency = (v) => v != null ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v) : "$0.00";
const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "\u2014";

function DealerSummaryCards({ dealer }) {
  const items = [
    { label: "Months Active", value: dealer.months_active },
    { label: "Total Gross", value: formatCurrency(dealer.total_gross_received) },
    { label: "Total Invoiced", value: formatCurrency(dealer.total_invoiced) },
    { label: "Latest Payment", value: formatDate(dealer.latest_payment_date) },
    { label: "Next Expected", value: formatDate(dealer.expected_next_payment_date) },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      {items.map((item) => (
        <Card key={item.label} className="kpi-card">
          <CardContent className="pt-0 pb-0">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{item.label}</p>
            <p className="font-display text-lg font-semibold mt-1 tabular-nums">{item.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PaymentsTab({ payments }) {
  return (
    <Card>
      <CardContent className="pt-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead className="text-right">Invoice Amt</TableHead>
              <TableHead>Transaction ID</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(payments || []).map((p) => (
              <TableRow key={p.id}>
                <TableCell className="text-sm">{formatDate(p.platform_received_at)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatCurrency(p.gross_amount)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatCurrency(p.invoice_amount)}</TableCell>
                <TableCell className="font-mono text-xs">{p.external_transaction_id}</TableCell>
                <TableCell><StatusBadge status={p.processing_status} /></TableCell>
              </TableRow>
            ))}
            {(!payments || payments.length === 0) && (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No payments</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function InvoicesTab({ invoices, onSync, onResend }) {
  return (
    <Card>
      <CardContent className="pt-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice #</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Issue Date</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead>Paid Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(invoices || []).map((inv) => (
              <TableRow key={inv.id}>
                <TableCell className="font-mono text-xs">{inv.stripe_invoice_number}</TableCell>
                <TableCell className="text-right tabular-nums">{formatCurrency(inv.invoice_amount)}</TableCell>
                <TableCell className="text-sm">{formatDate(inv.issue_date)}</TableCell>
                <TableCell className="text-sm">{formatDate(inv.due_date)}</TableCell>
                <TableCell className="text-sm">{formatDate(inv.paid_at)}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <StatusBadge status={inv.status} />
                    {inv.overdue_flag && inv.status !== "paid" && <StatusBadge status="overdue" />}
                  </div>
                </TableCell>
                <TableCell><StatusBadge status={inv.payment_method_expected} /></TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onSync(inv.id)} title="Sync" data-testid={`sync-invoice-${inv.id}`}>
                      <RefreshCw className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onResend(inv.id)} title="Resend" data-testid={`resend-invoice-${inv.id}`}>
                      <Mail className="w-3 h-3" />
                    </Button>
                    {inv.hosted_invoice_url && (
                      <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Open in Stripe">
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </a>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {(!invoices || invoices.length === 0) && (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No invoices</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default function DealerDetail() {
  const { dealerId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDealer = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dealers/${dealerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load dealer");
    } finally {
      setLoading(false);
    }
  }, [dealerId, token]);

  useEffect(() => { fetchDealer(); }, [fetchDealer]);

  const handleResendInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/resend`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Invoice resent");
    } catch (err) {
      toast.error("Failed to resend invoice");
    }
  };

  const handleSyncInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Invoice synced");
      fetchDealer();
    } catch (err) {
      toast.error("Failed to sync invoice");
    }
  };

  if (loading) return <div className="animate-pulse p-8">Loading dealer...</div>;
  if (!data) return <div className="p-8 text-muted-foreground">Dealer not found</div>;

  const { dealer, payments, invoices, alerts } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/dealers")} data-testid="back-to-dealers">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight" data-testid="dealer-name">{dealer.dealer_name}</h1>
          <p className="text-sm text-muted-foreground">Dealer since {formatDate(dealer.first_payment_date)}</p>
        </div>
        <div className="ml-auto"><StatusBadge status={dealer.status} /></div>
      </div>

      <DealerSummaryCards dealer={dealer} />

      <Card>
        <CardContent className="pt-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div><span className="text-muted-foreground">Email:</span><p className="font-medium">{dealer.email || "\u2014"}</p></div>
            <div><span className="text-muted-foreground">Primary Invoice Email:</span><p className="font-medium">{dealer.primary_invoice_email}</p></div>
            <div><span className="text-muted-foreground">Secondary Invoice Email:</span><p className="font-medium">{dealer.secondary_invoice_email}</p></div>
            <div><span className="text-muted-foreground">Stripe Customer:</span><p className="font-mono text-xs">{dealer.stripe_customer_id || "\u2014"}</p></div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="payments">
        <TabsList>
          <TabsTrigger value="payments" data-testid="dealer-tab-payments">Payments ({payments?.length || 0})</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="dealer-tab-invoices">Invoices ({invoices?.length || 0})</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="dealer-tab-alerts">Alerts ({alerts?.length || 0})</TabsTrigger>
        </TabsList>
        <TabsContent value="payments">
          <PaymentsTab payments={payments} />
        </TabsContent>
        <TabsContent value="invoices">
          <InvoicesTab invoices={invoices} onSync={handleSyncInvoice} onResend={handleResendInvoice} />
        </TabsContent>
        <TabsContent value="alerts">
          <Card>
            <CardContent className="pt-4">
              <div className="space-y-3">
                {(alerts || []).map((alert) => (
                  <div key={alert.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 border">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <StatusBadge status={alert.alert_type} />
                        <StatusBadge status={alert.severity} />
                        {alert.is_resolved && <StatusBadge status="synced" />}
                      </div>
                      <p className="text-sm">{alert.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">{formatDate(alert.created_at)}</p>
                    </div>
                  </div>
                ))}
                {(!alerts || alerts.length === 0) && (
                  <div className="text-center py-8 text-muted-foreground">No alerts for this dealer</div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
